/**
 * Repository search results — the controlled results body shown inside the
 * unified Repository overlay when the search box has a query or active tags.
 * Extracted from the former standalone `SearchModal` so the same search verb
 * (NN5) now lives *inside* the Repository rather than as a separate modal you
 * got teleported away from.
 *
 * Two backends, chosen by `isWorkshopBffEnabled()`:
 *   - BFF (unified): server-backed AI search across the caller's readable
 *     document collections (filename + body), plus a local `searchAll` pass
 *     over workshop records so TagFilter chips still narrow records.
 *     Eventually consistent: a just-saved record may lag the index (the
 *     Repository's own listing is the source of truth; search is discovery).
 *   - Local (R1 fallback): pure client-side substring filter over the
 *     workshop's captured content (`searchAll`), honouring `activeTags`.
 *
 * This component renders results ONLY — the search `<input>`, TagFilter, the
 * workshop filter, and the dialog chrome live in the host overlay, which owns
 * `query` / `activeTags` and decides what a clicked hit does (we're already
 * in the Repository, so a hit reveals its home rather than navigating away).
 */

import { type CSSProperties, useEffect, useMemo, useState } from "react";
import {
  Camera,
  FileText,
  FlaskConical,
  Lightbulb,
  ListChecks,
  Loader2,
  NotebookPen,
  Paperclip,
  ReceiptText,
  Scale,
  Telescope,
} from "lucide-react";
import { isWorkshopBffEnabled } from "@/env";
import type { Workshop } from "@/content/workshops";
import { useCaptures, type UserCapture } from "@/stores/useCaptures";
import { useDecisions, type Decision } from "@/stores/useDecisions";
import { useActions, type ActionItem } from "@/stores/useActions";
import { useAssumptions, type Assumption } from "@/stores/useAssumptions";
import { useTranscripts, type Transcript } from "@/stores/useTranscripts";
import { useAssetLocker, type Upload } from "@/stores/useAssetLocker";
import { useFindings, type Finding } from "@/stores/useFindings";
import { useOpportunities, type Opportunity } from "@/stores/useOpportunities";
import { useRecommendations, type Recommendation } from "@/stores/useRecommendations";
import { useFacilitationNotes, type FacilitationNote } from "@/stores/useFacilitationNotes";
import { searchAll, type SearchCategory, type SearchHit } from "@/lib/search";
import {
  DocsApiError,
  searchDocs,
  type SearchHit as DocsSearchHit,
} from "@/lib/docs-api";

type RepositorySearchResultsProps = {
  /** The active query string (owned by the host overlay). */
  query: string;
  /** Workshop-record tag facet (AND). Docs are not tagged. */
  activeTags?: readonly string[];
  /** Scopes the local search + labels the result set. */
  workshopId: Workshop["id"];
  /** A local record hit was clicked — host reveals its home surface. */
  onOpenRecordHit: (hit: SearchHit) => void;
  /** A BFF document hit was clicked — host opens a document preview. */
  onOpenDocHit: (hit: DocsSearchHit) => void;
};

const EMPTY_CAPTURES: readonly UserCapture[] = Object.freeze([]);
const EMPTY_DECISIONS: readonly Decision[] = Object.freeze([]);
const EMPTY_ACTIONS: readonly ActionItem[] = Object.freeze([]);
const EMPTY_ASSUMPTIONS: readonly Assumption[] = Object.freeze([]);
const EMPTY_TRANSCRIPTS: readonly Transcript[] = Object.freeze([]);
const EMPTY_UPLOADS: readonly Upload[] = Object.freeze([]);
const EMPTY_FINDINGS: readonly Finding[] = Object.freeze([]);
const EMPTY_OPPORTUNITIES: readonly Opportunity[] = Object.freeze([]);
const EMPTY_RECOMMENDATIONS: readonly Recommendation[] = Object.freeze([]);
const EMPTY_NOTES: readonly FacilitationNote[] = Object.freeze([]);

const MUTED_HINT: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-muted)",
  letterSpacing: "0.5px",
};

const CATEGORY_META: Record<
  SearchCategory,
  { label: string; icon: typeof Camera }
> = {
  capture: { label: "Captures", icon: Camera },
  decision: { label: "Decisions", icon: Scale },
  action: { label: "Actions", icon: ListChecks },
  assumption: { label: "Assumptions", icon: FlaskConical },
  transcript: { label: "Transcripts", icon: FileText },
  upload: { label: "Uploads", icon: Paperclip },
  finding: { label: "Findings", icon: Telescope },
  opportunity: { label: "Opportunities", icon: Lightbulb },
  recommendation: { label: "Recommendations", icon: ReceiptText },
  note: { label: "Facilitation notes", icon: NotebookPen },
};

function RecordGroups({
  groups,
  onOpenRecordHit,
}: {
  groups: Array<{ category: SearchCategory; hits: SearchHit[] }>;
  onOpenRecordHit: (hit: SearchHit) => void;
}) {
  return (
    <>
      {groups
        .filter((g) => g.hits.length > 0)
        .map((g) => {
          const Icon = CATEGORY_META[g.category].icon;
          return (
            <section key={g.category}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "var(--fs-label)",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: "var(--text-secondary)",
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                <Icon
                  aria-hidden
                  style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }}
                />
                {CATEGORY_META[g.category].label} ({g.hits.length})
              </div>
              <ul className="flex flex-col gap-1.5">
                {g.hits.map((hit) => (
                  <li key={`${hit.category}-${hit.id}`}>
                    <button
                      type="button"
                      onClick={() => onOpenRecordHit(hit)}
                      className="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left cursor-pointer transition-colors border-[rgba(var(--accent-true-blue-rgb),0.12)] bg-[rgba(var(--accent-true-blue-rgb),0.04)] hover:bg-[rgba(var(--accent-true-blue-rgb),0.10)] hover:border-[rgba(var(--accent-true-blue-rgb),0.30)] focus-visible:outline-none focus-visible:border-[var(--accent-true-blue)] focus-visible:bg-[rgba(var(--accent-true-blue-rgb),0.10)]"
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: "var(--fs-base)",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {hit.title}
                        </div>
                        {hit.subtitle && (
                          <div
                            style={{
                              fontSize: "var(--fs-label)",
                              color: "var(--text-muted)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {hit.subtitle}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
    </>
  );
}

export function RepositorySearchResults({
  query,
  activeTags = [],
  workshopId,
  onOpenRecordHit,
  onOpenDocHit,
}: RepositorySearchResultsProps) {
  const wsId = workshopId;
  const bffMode = isWorkshopBffEnabled();
  const hasQuery = query.trim().length > 0;
  const hasActiveSearch = hasQuery || activeTags.length > 0;

  const captures = useCaptures(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_CAPTURES as UserCapture[]),
  );
  const decisions = useDecisions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_DECISIONS as Decision[]),
  );
  const actions = useActions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ACTIONS as ActionItem[]),
  );
  const assumptions = useAssumptions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ASSUMPTIONS as Assumption[]),
  );
  const transcripts = useTranscripts(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_TRANSCRIPTS as Transcript[]),
  );
  const uploads = useAssetLocker(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_UPLOADS as Upload[]),
  );
  const findings = useFindings(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_FINDINGS as Finding[]),
  );
  const opportunities = useOpportunities(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_OPPORTUNITIES as Opportunity[]),
  );
  const recommendations = useRecommendations(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_RECOMMENDATIONS as Recommendation[]),
  );
  const notes = useFacilitationNotes(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_NOTES as FacilitationNote[]),
  );

  // Prime local stores for record search (local mode always; BFF mode so
  // TagFilter / record hits still work alongside document searchDocs).
  useEffect(() => {
    void useCaptures.getState().hydrate();
    void useAssetLocker.getState().hydrate();
    void useDecisions.getState().hydrate();
    void useActions.getState().hydrate();
    void useAssumptions.getState().hydrate();
    void useTranscripts.getState().hydrate();
    void useFindings.getState().hydrate();
    void useOpportunities.getState().hydrate();
    void useRecommendations.getState().hydrate();
    void useFacilitationNotes.getState().hydrate();
  }, []);

  const results = useMemo(
    () =>
      searchAll(
        query,
        {
          captures,
          decisions,
          actions,
          assumptions,
          transcripts,
          uploads,
          findings,
          opportunities,
          recommendations,
          notes,
        },
        { activeTags },
      ),
    [
      query,
      activeTags,
      captures,
      decisions,
      actions,
      assumptions,
      transcripts,
      uploads,
      findings,
      opportunities,
      recommendations,
      notes,
    ],
  );

  // BFF unified-search state (used only when bffMode). Lazy-init to "loading"
  // when mounted with a query so the first paint never flashes "No matches"
  // before the debounced search resolves.
  const [bffStatus, setBffStatus] = useState<"idle" | "loading" | "error">(() =>
    bffMode && hasQuery ? "loading" : "idle",
  );
  const [bffResults, setBffResults] = useState<DocsSearchHit[]>([]);
  const [bffCollections, setBffCollections] = useState<string[]>([]);
  const [bffError, setBffError] = useState<string | null>(null);

  // Debounced server search — avoids an authed POST per keystroke, and the
  // `ignore` guard stops a slow earlier response overwriting a newer query.
  // Tag-only searches skip the server (docs have no user tags).
  useEffect(() => {
    if (!bffMode) return;
    const q = query.trim();
    if (!q) {
      setBffStatus("idle");
      setBffResults([]);
      setBffCollections([]);
      setBffError(null);
      return;
    }
    let ignore = false;
    setBffStatus("loading");
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await searchDocs(q, 8);
          if (ignore) return;
          setBffResults(res.results);
          setBffCollections(res.collections_searched);
          setBffStatus("idle");
          setBffError(null);
        } catch (err) {
          if (ignore) return;
          setBffResults([]);
          setBffStatus("error");
          setBffError(
            err instanceof DocsApiError || err instanceof Error
              ? err.message
              : "Search failed",
          );
        }
      })();
    }, 300);
    return () => {
      ignore = true;
      clearTimeout(handle);
    };
  }, [bffMode, query]);

  const groups: Array<{ category: SearchCategory; hits: SearchHit[] }> = [
    { category: "capture", hits: results.captures },
    { category: "decision", hits: results.decisions },
    { category: "action", hits: results.actions },
    { category: "assumption", hits: results.assumptions },
    { category: "transcript", hits: results.transcripts },
    { category: "upload", hits: results.uploads },
    { category: "finding", hits: results.findings },
    { category: "opportunity", hits: results.opportunities },
    { category: "recommendation", hits: results.recommendations },
    { category: "note", hits: results.notes },
  ];

  // ── BFF: documents via searchDocs + local workshop records (tag-aware) ──
  if (bffMode) {
    const docsErrored = hasQuery && bffStatus === "error";
    const docsLoading = hasQuery && bffStatus === "loading";
    const showNoMatches =
      hasActiveSearch &&
      !docsLoading &&
      !docsErrored &&
      bffResults.length === 0 &&
      results.total === 0;

    return (
      <div className="flex flex-col gap-3">
        {!hasActiveSearch && (
          <p style={MUTED_HINT}>
            Search documents and everything workshops capture — across what your
            role can access. Filter workshop records by tag.
          </p>
        )}
        {docsLoading && (
          <p style={MUTED_HINT} className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Searching…
          </p>
        )}
        {docsErrored && (
          <p style={{ fontSize: "var(--fs-label)", color: "var(--accent-red)" }}>
            Document search is unavailable{bffError ? `: ${bffError}` : "."}
          </p>
        )}
        {showNoMatches && <p style={MUTED_HINT}>No matches.</p>}
        {hasQuery && bffStatus === "idle" && bffResults.length > 0 && (
          <>
            <p style={MUTED_HINT}>
              {bffResults.length} document result{bffResults.length === 1 ? "" : "s"}{" "}
              across {bffCollections.join(", ") || "no collections"}
            </p>
            <ul className="flex flex-col gap-1.5">
              {bffResults.map((hit, i) => (
                <li key={`${hit.filename}-${i}`}>
                  <button
                    type="button"
                    onClick={() => onOpenDocHit(hit)}
                    className="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left cursor-pointer transition-colors border-[rgba(var(--accent-true-blue-rgb),0.12)] bg-[rgba(var(--accent-true-blue-rgb),0.04)] hover:bg-[rgba(var(--accent-true-blue-rgb),0.10)] hover:border-[rgba(var(--accent-true-blue-rgb),0.30)] focus-visible:outline-none focus-visible:border-[var(--accent-true-blue)] focus-visible:bg-[rgba(var(--accent-true-blue-rgb),0.10)]"
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: "var(--fs-base)",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                        }}
                      >
                        <FileText
                          aria-hidden
                          style={{
                            width: 14,
                            height: 14,
                            color: "var(--text-muted)",
                            flex: "none",
                          }}
                        />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {hit.filename}
                        </span>
                        {hit.page ? (
                          <span
                            style={{
                              fontSize: "var(--fs-micro)",
                              color: "var(--text-muted)",
                            }}
                          >
                            · p{hit.page}
                          </span>
                        ) : null}
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: "var(--fs-micro)",
                            color: "var(--text-muted)",
                          }}
                        >
                          {hit.score.toFixed(3)}
                        </span>
                      </div>
                      <p
                        className="line-clamp-2"
                        style={{
                          marginTop: 2,
                          fontSize: "var(--fs-label)",
                          color: "var(--text-muted)",
                        }}
                      >
                        {hit.content}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <p
              style={{
                fontSize: "var(--fs-micro)",
                color: "var(--text-muted)",
                letterSpacing: "0.5px",
              }}
            >
              Newly saved items may take a moment to appear in search.
            </p>
          </>
        )}
        {results.total > 0 && (
          <div className="flex flex-col gap-4">
            {hasQuery && bffResults.length > 0 && (
              <p style={MUTED_HINT}>Workshop records</p>
            )}
            <RecordGroups groups={groups} onOpenRecordHit={onOpenRecordHit} />
          </div>
        )}
      </div>
    );
  }

  // ── Local substring search results (R1 fallback) ──
  return (
    <div className="flex flex-col gap-4">
      {hasActiveSearch && results.total === 0 && (
        <p style={MUTED_HINT}>No matches.</p>
      )}
      {hasActiveSearch && (
        <RecordGroups groups={groups} onOpenRecordHit={onOpenRecordHit} />
      )}
    </div>
  );
}
